import { HugeiconsIcon } from '@hugeicons/react'
import {
  AlertCircleIcon,
  CheckmarkCircle01Icon,
} from '@hugeicons/core-free-icons'
import { useEffect, useState } from 'react'
import type { FormDialogOptions } from '@/contexts/form-dialog-context'
import type { AnyReactFormApi } from '@/types/form'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'

interface FormDialogProps<TFormData, TResponse> {
  open: boolean
  onOpenChange: (open: boolean) => void
  options: FormDialogOptions<TFormData, TResponse> | null
  form: AnyReactFormApi
  isLoading: boolean
  error: Error | null
  successData: TResponse | null
}

export function FormDialog<TFormData, TResponse>({
  open,
  onOpenChange,
  options,
  form,
  isLoading,
  error,
  successData,
}: FormDialogProps<TFormData, TResponse>) {
  if (!options) return null

  // Small internal component to subscribe to the TanStack form and
  // force a re-render when form values change so the submit button
  // disabled state (options.isSubmitDisabled) stays reactive.
  // eslint-disable-next-line no-shadow
  function FormStateSubscriber({ form }: { form: AnyReactFormApi }) {
    const [, setTick] = useState(0)

    useEffect(() => {
      const unsubscribe = form.store.subscribe(() => setTick((t) => t + 1))
      return () => unsubscribe()
    }, [form])

    return null
  }

  const {
    title,
    description,
    submitText = 'Submit',
    cancelText = 'Cancel',
    submitVariant = 'default',
    children,
    successTitle,
    successDescription,
    onSuccess,
    showSuccessState: shouldShowSuccessState = true, // Default to true
  } = options

  const handleCancel = () => {
    onOpenChange(false)
  }

  // Check if we should show success state
  const showSuccessState = successData !== null && shouldShowSuccessState
  const customSuccessContent =
    showSuccessState && onSuccess ? onSuccess(successData) : null

  // Use success title/description if available, otherwise use original
  const displayTitle = showSuccessState && successTitle ? successTitle : title
  const displayDescription =
    showSuccessState && successDescription ? successDescription : description

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{displayTitle}</DialogTitle>
          {displayDescription && (
            <DialogDescription>{displayDescription}</DialogDescription>
          )}
        </DialogHeader>

        {/* Success State */}
        {showSuccessState ? (
          <div className="space-y-4">
            {customSuccessContent ? (
              // Custom success UI provided by consumer
              <>
                <div className="py-4">{customSuccessContent}</div>
                <DialogFooter>
                  <Button onClick={handleCancel}>Close</Button>
                </DialogFooter>
              </>
            ) : (
              // Default success UI
              <>
                <Alert>
                  <HugeiconsIcon
                    icon={CheckmarkCircle01Icon}
                    className="h-4 w-4"
                  />
                  <AlertDescription>
                    Operation completed successfully.
                  </AlertDescription>
                </Alert>
                <DialogFooter>
                  <Button onClick={handleCancel}>Close</Button>
                </DialogFooter>
              </>
            )}
          </div>
        ) : (
          // Form State
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              form.handleSubmit()
            }}
          >
            {/* Subscribe to form changes so this component re-renders when values change.
                  This allows options.isSubmitDisabled(form) to be evaluated reactively. */}
            {/** Use a ticking state that updates when the form notifies subscribers. */}
            <FormStateSubscriber form={form} />
            <div className="space-y-4 py-4">
              {/* Error Display */}
              {error && (
                <Alert variant="destructive">
                  <HugeiconsIcon icon={AlertCircleIcon} className="h-4 w-4" />
                  <AlertDescription>{error.message}</AlertDescription>
                </Alert>
              )}

              {/* Form Fields - Rendered by consumer */}
              {children(form)}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
              >
                {cancelText}
              </Button>
              <Button
                type="submit"
                variant={submitVariant}
                disabled={
                  isLoading ||
                  (options.isSubmitDisabled
                    ? options.isSubmitDisabled(form)
                    : false)
                }
              >
                {isLoading ? 'Submitting...' : submitText}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
